/** «WB: регионы» (`/wb-regions`, право `wb_regions`): дефицит и перекос остатка WB по регионам
 *  «сейчас», путь поставок, проверки сходимости по витринам dwh `mart.wb_region_*` (бэкенд
 *  `wb_regions/`) и «Подбор поставки» (дерево товар → регионы, цели галочками → Excel). Всё состояние — в адресе
 *  (state.ts; черновик и цели подбора — в localStorage). Портал ничего не пересчитывает: цвет —
 *  `deficit_class` витрины, агрегаты — бэкенд; исключения — «Заполнить до N дней» подбора
 *  (supplyDraft.ts) и суммы товара подбора по включённым регионам (draftTree.ts); ошибка любой ручки — текст бэкенда вместо вкладки, без цифр. */
import React, { useCallback, useEffect } from "react";
import { useSearchParams } from "react-router-dom";

import { WB_ALL, type WbMeta } from "@/api/wbRegions";
import { cn } from "@/lib/utils";
import { useBuildSync } from "./buildSync";
import { ErrorBox, Loading, Seg } from "./cells";
import { FreshnessBar } from "./FreshnessBar";
import { badRegions } from "./matrix";
import { ChecksTab } from "./ChecksTab";
import { OfferCard } from "./OfferCard";
import { OfferMatrix } from "./OfferMatrix";
import {
  useWbChecks,
  useWbChecksHistory,
  useWbMeta,
  useWbOffers,
  useWbRegions,
  useWbSupplies,
} from "./queries";
import { RegionsTable } from "./RegionsTable";
import { SuppliesTab } from "./SuppliesTab";
import { SupplyCard } from "./SupplyCard";
import { SupplyDraftTab } from "./SupplyDraftTab";
import {
  applyPatch,
  cardValue,
  legalStatus,
  readState,
  supplyQuery,
  TABS,
  type CardRef,
  type Patch,
  type PageState,
} from "./state";

const TITLE = "WB: остатки по регионам";
const RED_BOX =
  "rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-400";

function ErrorWithReset({
  testId,
  text,
  button,
  onReset,
}: {
  testId: string;
  text: string;
  button: string;
  onReset: () => void;
}): React.ReactElement {
  return (
    <div
      role="alert"
      data-testid={testId}
      className={cn("flex flex-wrap items-center gap-3", RED_BOX)}
    >
      <span>{text}</span>
      <button
        type="button"
        className="rounded border border-red-300 bg-background px-2 py-0.5 text-foreground hover:bg-accent dark:border-red-900"
        onClick={onReset}
      >
        {button}
      </button>
    </div>
  );
}

export function WbRegionsPage(): React.ReactElement {
  const [params, setParams] = useSearchParams();
  const state = readState(params);
  const meta = useWbMeta();
  const patch = useCallback(
    (p: Patch) => setParams((prev) => applyPatch(prev, p), { replace: true }),
    [setParams],
  );

  const active = meta.data?.legals.filter((l) => l.active).map((l) => l.legal_name) ?? [];
  const status = meta.data ? legalStatus(state.legal, active) : null;
  // кабинета в адресе нет — первый активный из /meta (ссылка стабильна)
  const first = status?.kind === "missing" ? status.first : null;
  useEffect(() => {
    if (first) patch({ legal: first });
  }, [first, patch]);

  return (
    <div className="mx-auto max-w-none space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-bold">{TITLE}</h1>
        {meta.data && (
          <Seg
            label="Кабинет"
            value={status?.kind === "ok" ? status.legal : null}
            options={[
              ...active.map((l) => ({ id: l, label: l })),
              { id: WB_ALL, label: "Оба кабинета" },
            ]}
            onChange={(v) => patch({ legal: v, card: null })}
          />
        )}
      </div>
      {meta.isLoading && <Loading text="Загружаю свежесть витрин…" />}
      {meta.isError && <ErrorBox error={meta.error} what="свежесть витрин (/meta)" />}
      {meta.data && status?.kind === "missing" && status.first === null && (
        <div role="alert" className={RED_BOX} data-testid="no-legals">
          В витрине нет активных кабинетов WB — показывать нечего.
        </div>
      )}
      {meta.data && status?.kind === "unknown" && (
        <ErrorWithReset
          testId="unknown-legal"
          text={`Кабинета «${status.legal}» нет среди активных (есть: ${[...active, "оба"].join(", ")}).`}
          button="Открыть первый кабинет"
          onReset={() => patch({ legal: null, card: null })}
        />
      )}
      {meta.data && status?.kind === "ok" && (
        <Loaded meta={meta.data} legal={status.legal} state={state} patch={patch} setParams={setParams} />
      )}
    </div>
  );
}

function Loaded({
  meta,
  legal,
  state,
  patch,
  setParams,
}: {
  meta: WbMeta;
  legal: string;
  state: PageState;
  patch: (p: Patch) => void;
  setParams: ReturnType<typeof useSearchParams>[1];
}): React.ReactElement {
  const [params] = useSearchParams();
  const tab = state.tab;
  const isDeficit = tab.tab === "deficit";
  const isDraft = tab.tab === "draft";
  const regions = useWbRegions(isDeficit ? legal : null);
  // подбор поставки: все офферы кабинета (поиск — на фронте, отмеченные не теряются при поиске)
  const offers = useWbOffers(isDeficit || isDraft ? legal : null, isDraft ? "" : state.q);
  const isSupplies = tab.tab === "supplies";
  const isChecks = tab.tab === "checks";
  const sq = supplyQuery(state.supply, meta.supply_defaults, meta.status_filters);
  const supplies = useWbSupplies(isSupplies && sq.query ? legal : null, sq.query);
  const checks = useWbChecks(isChecks ? legal : null);
  const checksHistory = useWbChecksHistory(isChecks ? legal : null);
  const shownLegals = meta.legals.filter((l) => l.active && (legal === WB_ALL || l.legal_name === legal));

  // открытие карточки — новая запись истории («Назад» браузера закрывает панель)
  const openCard = useCallback(
    (c: CardRef) => setParams((prev) => applyPatch(prev, { card: cardValue(c) }), { replace: false }),
    [setParams],
  );
  const closeCard = useCallback(() => patch({ card: null }), [patch]);

  const states = [
    meta.state,
    ...(isDeficit ? [regions.data?.state, offers.data?.state] : []),
    ...(isDraft ? [offers.data?.state] : []),
    ...(isSupplies ? [supplies.data?.state] : []),
    ...(isChecks ? [checks.data?.state, checksHistory.data?.state] : []),
  ].filter(
    (s): s is NonNullable<typeof s> => Boolean(s),
  );
  // ответы разных сборок (кэшируются отдельно) — сброс кэша раздела и плашка
  const mixed = useBuildSync(states);
  const bad = badRegions([
    ...meta.regions,
    ...(regions.data?.rows ?? []),
    ...(offers.data?.offers.flatMap((o) => o.regions) ?? []),
  ]);

  return (
    <>
      <FreshnessBar
        meta={meta}
        legals={shownLegals}
        states={states}
        mixed={mixed}
        checksSearch={applyPatch(params, { tab: "checks", card: null }).toString()}
      />
      <div className="flex gap-4 border-b" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab.tab === t.id}
            onClick={() => patch({ tab: t.id === "deficit" ? null : t.id })}
            className={cn(
              "-mb-px border-b-2 px-1 pb-2 text-sm font-medium transition-colors",
              tab.tab === t.id
                ? "border-primary text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground",
            )}
          >
            {t.label}
          </button>
        ))}
      </div>
      {tab.tab === null && (
        <ErrorWithReset
          testId="unknown-tab"
          text={`Неизвестная вкладка «${tab.unknown}» в адресе.`}
          button="Открыть «Дефицит»"
          onReset={() => patch({ tab: null })}
        />
      )}
      {isSupplies && (
        <SuppliesTab
          meta={meta}
          legal={legal}
          params={state.supply}
          q={sq}
          supplies={supplies}
          onPatch={patch}
          onOpenCard={openCard}
        />
      )}
      {isChecks && <ChecksTab legal={legal} checks={checks} history={checksHistory} onOpenCard={openCard} />}
      {state.card.error && (
        <ErrorWithReset
          testId="bad-card"
          text={state.card.error}
          button="Закрыть"
          onReset={closeCard}
        />
      )}
      {bad.length > 0 && (
        <div role="alert" className={RED_BOX} data-testid="bad-regions">
          Витрина отдала регионы вне контракта (не показаны в столбцах матрицы): {bad.join("; ")}.
        </div>
      )}
      {isDeficit &&
        (regions.isError || offers.isError ? (
          <div className="space-y-2">
            {regions.isError && <ErrorBox error={regions.error} what="регионы (/regions)" />}
            {offers.isError && <ErrorBox error={offers.error} what="офферы (/offers)" />}
          </div>
        ) : !regions.data || !offers.data ? (
          <Loading text="Загружаю остатки по регионам…" />
        ) : (
          <div className="space-y-4">
            <RegionsTable
              data={regions.data}
              focus={state.focus}
              onFocus={(k) => patch({ focus: k })}
              abroadOpen={state.abroadOpen}
              onToggleAbroad={() => patch({ abroad: state.abroadOpen ? null : "1" })}
            />
            <OfferMatrix
              data={offers.data}
              metaRegions={meta.regions}
              q={state.q}
              deficitOnly={state.deficitOnly}
              hideNoDemand={state.hideNoDemand}
              focus={state.focus}
              searching={offers.isPlaceholderData}
              showLegal={legal === WB_ALL}
              onPatch={patch}
              onOpenCard={openCard}
            />
          </div>
        ))}
      {isDraft &&
        (offers.isError ? (
          <ErrorBox error={offers.error} what="офферы (/offers)" />
        ) : !offers.data || offers.isPlaceholderData ? (
          // прежний ответ с поиском «Дефицита» — не полный список: не показываем и не выгружаем
          <Loading text="Загружаю офферы по регионам…" />
        ) : (
          <SupplyDraftTab
            data={offers.data}
            params={state.draft}
            q={state.q}
            deficitOnly={state.deficitOnly}
            onPatch={patch}
          />
        ))}
      {state.card.card?.kind === "offer" && (
        <OfferCard card={state.card.card} pageState={meta.state} onClose={closeCard} onOpenCard={openCard} />
      )}
      {state.card.card?.kind === "supply" && (
        <SupplyCard card={state.card.card} pageState={meta.state} onClose={closeCard} onOpenCard={openCard} />
      )}
    </>
  );
}
